import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Video, FileText, X, Link, Monitor, BookOpen, Mic, Volume2, Play, Pause } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface CreateLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLessonCreated: () => void;
}

const CreateLessonModal = ({ isOpen, onClose, onLessonCreated }: CreateLessonModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tutorProfileExists, setTutorProfileExists] = useState<boolean>(false);
  const [approvedSubjects, setApprovedSubjects] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    difficulty: '',
    duration: '',
    price: '',
    materials: '',
    content: '',
    status: 'published',
    lessonType: 'text' // Add lesson type field
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoUploadType, setVideoUploadType] = useState<'file' | 'url'>('file');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [audioUploadType, setAudioUploadType] = useState<'file' | 'url'>('file');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioPreviewRef, setAudioPreviewRef] = useState<HTMLAudioElement | null>(null);

  const defaultSubjects = [
    "Mathematics", 
    "English", 
    "Spanish", 
    "Physics", 
    "Chemistry", 
    "Biology", 
    "Literature", 
    "Conversation",
    "Programming",
    "Music",
    "Travel",
    "Fitness & Bodybuilding",
    "العلاقات العامة",
    "Others"
  ];

  // Combine default subjects with approved subjects
  const allSubjects = [...new Set([...defaultSubjects.filter(s => s !== "Others"), ...approvedSubjects, "Others"])];
  const difficulties = ["Beginner", "Intermediate", "Advanced"];

  // Rich text editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
      [{ 'direction': 'rtl' }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align', 'direction', 'blockquote', 'code-block',
    'link', 'image'
  ];

  // Fetch approved subjects from PocketBase
  useEffect(() => {
    const fetchApprovedSubjects = async () => {
      try {
        // Fetch from approved_subjects collection
        const approvedResult = await pb.collection('approved_subjects').getList(1, 100, {
          requestKey: null
        });
        
        // Fetch from subject_requests with approved status
        const requestsResult = await pb.collection('subject_requests').getList(1, 100, {
          filter: 'status = "approved"',
          requestKey: null
        });
        
        const approvedNames = approvedResult.items.map((item: any) => item.name || item.subjectName).filter(Boolean);
        const requestNames = requestsResult.items.map((item: any) => item.name || item.subjectName).filter(Boolean);
        
        setApprovedSubjects([...new Set([...approvedNames, ...requestNames])]);
      } catch (error) {
        console.log('Could not fetch approved subjects:', error);
      }
    };
    
    if (isOpen) {
      fetchApprovedSubjects();
    }
  }, [isOpen]);

  // Check if tutor profile exists when modal opens
  useEffect(() => {
    const checkTutorProfile = async () => {
      if (!user?.id || user?.role !== 'tutor') return;
      
      try {
        const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
          filter: `user = "${user.id}"`,
        });
        
        if (tutorProfiles.items.length > 0) {
          setTutorProfileExists(true);
          console.log('Tutor profile exists for user:', user.id);
        } else {
          setTutorProfileExists(false);
          console.error('No tutor profile found for user:', user.id);
          toast({
            title: "Error",
            description: "No tutor profile found. Please complete your tutor profile first.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error checking tutor profile:', error);
        setTutorProfileExists(false);
        toast({
          title: "Error",
          description: "Failed to load tutor profile",
          variant: "destructive"
        });
      }
    };

    if (isOpen) {
      checkTutorProfile();
    }
  }, [isOpen, user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('Video file selected:', file); // Debug log
    if (file) {
      console.log('File type:', file.type); // Debug log
      if (file.type.startsWith('video/')) {
        setVideoFile(file);
        setVideoUrl('');
        console.log('Video file set successfully:', file.name); // Debug log
        toast({
          title: "Success",
          description: `Video file "${file.name}" selected successfully`,
          variant: "default"
        });
      } else {
        console.log('Invalid file type:', file.type); // Debug log
        toast({
          title: "Error",
          description: "Please select a video file",
          variant: "destructive"
        });
      }
    } else {
      console.log('No file selected'); // Debug log
    }
  };

  const handleVideoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVideoUrl(e.target.value);
    setVideoFile(null);
  };

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachmentFile(file);
      toast({
        title: "Success",
        description: `File "${file.name}" selected successfully`,
        variant: "default"
      });
    }
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
  };

  const clearVideo = () => {
    setVideoFile(null);
    setVideoUrl('');
  };

  const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('audio/')) {
        setAudioFile(file);
        setAudioUrl('');
        toast({
          title: "Success",
          description: `Audio file "${file.name}" selected successfully`,
          variant: "default"
        });
      } else {
        toast({
          title: "Error",
          description: "Please select an audio file",
          variant: "destructive"
        });
      }
    }
  };

  const handleAudioUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAudioUrl(e.target.value);
    setAudioFile(null);
  };

  const clearAudio = () => {
    setAudioFile(null);
    setAudioUrl('');
    if (audioPreviewRef) {
      audioPreviewRef.pause();
      setIsAudioPlaying(false);
    }
  };

  const toggleAudioPreview = () => {
    if (!audioPreviewRef) return;
    
    if (isAudioPlaying) {
      audioPreviewRef.pause();
      setIsAudioPlaying(false);
    } else {
      audioPreviewRef.play();
      setIsAudioPlaying(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !tutorProfileExists) {
      toast({
        title: "Error",
        description: "Tutor profile not found. Please try again.",
        variant: "destructive"
      });
      return;
    }

    // Validate required fields with detailed error messages
    const missingFields = [];
    if (!formData.title.trim()) missingFields.push("Lesson Title");
    if (!formData.description.trim()) missingFields.push("Short Description");
    if (!formData.subject) missingFields.push("Subject");
    if (!formData.difficulty) missingFields.push("Difficulty Level");
    if (!formData.content.trim()) missingFields.push("Lesson Content");
    
    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill in the following required fields: ${missingFields.join(", ")}`,
        variant: "destructive"
      });
      return;
    }

    // Validate difficulty
    if (!difficulties.includes(formData.difficulty)) {
      toast({
        title: "Error",
        description: "Please select a valid difficulty level",
        variant: "destructive"
      });
      return;
    }

    // Validate subject - allow any subject from allSubjects list
    if (!allSubjects.includes(formData.subject)) {
      toast({
        title: "Error",
        description: "Please select a valid subject",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const lessonData = new FormData();
      lessonData.append('title', formData.title.trim());
      lessonData.append('description', formData.description.trim());
      lessonData.append('subject', formData.subject);
      lessonData.append('difficulty', formData.difficulty);
      
      // Ensure numeric fields are properly handled
      if (formData.duration) {
        const duration = parseInt(formData.duration, 10);
        if (!isNaN(duration) && duration > 0) {
          lessonData.append('duration', duration.toString());
        }
      }
      
      if (formData.price) {
        const price = parseFloat(formData.price);
        if (!isNaN(price) && price >= 0) {
          lessonData.append('price', price.toString());
        }
      }
      
      lessonData.append('materials', formData.materials.trim());
      lessonData.append('content', formData.content.trim());
      lessonData.append('tutorId', user.id); // Use user ID for the relation
      lessonData.append('status', 'published'); // Always use valid status
      lessonData.append('lessonType', formData.lessonType); // Add lesson type

      console.log('Video upload type:', videoUploadType); // Debug log
      console.log('Video file:', videoFile); // Debug log
      console.log('Video URL:', videoUrl); // Debug log

      if (videoUploadType === 'file' && videoFile) {
        console.log('Appending video file to form data:', videoFile.name); // Debug log
        lessonData.append('videoFile', videoFile);
      } else if (videoUploadType === 'url' && videoUrl.trim()) {
        console.log('Appending video URL to form data:', videoUrl.trim()); // Debug log
        lessonData.append('videoUrl', videoUrl.trim());
      }

      if (attachmentFile) {
        console.log('Appending attachment file to form data:', attachmentFile.name); // Debug log
        lessonData.append('attachment', attachmentFile);
      }

      // Handle audio uploads
      if (audioUploadType === 'file' && audioFile) {
        console.log('Appending audio file to form data:', audioFile.name); // Debug log
        lessonData.append('audioFile', audioFile);
      } else if (audioUploadType === 'url' && audioUrl.trim()) {
        console.log('Appending audio URL to form data:', audioUrl.trim()); // Debug log
        lessonData.append('audioUrl', audioUrl.trim());
      }

      console.log('Creating lesson with tutorId (user ID):', user.id);
      console.log('Form data being sent:', {
        title: formData.title.trim(),
        description: formData.description.trim(),
        subject: formData.subject,
        difficulty: formData.difficulty,
        status: 'published',
        tutorId: user.id
      });

      const createdLesson = await pb.collection('lesson_library').create(lessonData);
      console.log('Lesson created successfully:', createdLesson);

      toast({
        title: "Success",
        description: "Lesson created successfully!"
      });

      onLessonCreated();
      resetForm();
    } catch (error: any) {
      console.error('Error creating lesson:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      toast({
        title: "Error",
        description: error.message || "Failed to create lesson",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      subject: '',
      difficulty: '',
      duration: '',
      price: '',
      materials: '',
      content: '',
      status: 'published',
      lessonType: 'text'
    });
    setVideoFile(null);
    setVideoUrl('');
    setVideoUploadType('file');
    setAttachmentFile(null);
    setAudioFile(null);
    setAudioUrl('');
    setAudioUploadType('file');
    setIsAudioPlaying(false);
    if (audioPreviewRef) {
      audioPreviewRef.pause();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Create New Lesson</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-6">
            {/* Basic Info Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="title">Lesson Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter lesson title"
                  required
                />
              </div>

              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Select value={formData.subject} onValueChange={(value) => handleInputChange('subject', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {allSubjects.map(subject => (
                      <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="difficulty">Difficulty Level *</Label>
                <Select value={formData.difficulty} onValueChange={(value) => handleInputChange('difficulty', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    {difficulties.map(level => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="lessonType">Lesson Type *</Label>
                <Select value={formData.lessonType} onValueChange={(value) => handleInputChange('lessonType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select lesson type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Text-based Lesson
                      </div>
                    </SelectItem>
                    <SelectItem value="audio">
                      <div className="flex items-center gap-2">
                        <Volume2 className="h-4 w-4" />
                        Audio Lesson
                      </div>
                    </SelectItem>
                    <SelectItem value="video">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4" />
                        Video Lesson
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="30"
                  />
                </div>
                <div>
                  <Label htmlFor="price">Price ($)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Short Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description for the lesson preview"
                rows={3}
                required
              />
            </div>

            {/* Main Content with Rich Text Editor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BookOpen className="mr-2 h-5 w-5" />
                  Main Lesson Content *
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(content) => handleInputChange('content', content)}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write your detailed lesson content here. Use the formatting tools above to style your text..."
                    style={{ 
                      minHeight: '300px'
                    }}
                  />
                  <style>
                    {`.ql-editor { min-height: 250px !important; }`}
                  </style>
                  <p className="text-xs text-gray-500 mt-2">
                    Use the formatting toolbar to add headers, bold text, colors, lists, and more to create engaging lesson content.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Additional Materials */}
            <div>
              <Label htmlFor="materials">Additional Materials & Notes</Label>
              <Textarea
                id="materials"
                value={formData.materials}
                onChange={(e) => handleInputChange('materials', e.target.value)}
                placeholder="Any additional notes, required materials, or homework instructions"
                rows={3}
              />
            </div>
          </div>

          {/* Media Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Video className="mr-2 h-5 w-5" />
                  Video Content (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={videoUploadType === 'file' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setVideoUploadType('file')}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload File
                  </Button>
                  <Button
                    type="button"
                    variant={videoUploadType === 'url' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setVideoUploadType('url')}
                    className="flex items-center gap-2"
                  >
                    <Link className="h-4 w-4" />
                    Add URL
                  </Button>
                </div>

                {videoUploadType === 'file' ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      id="video-upload"
                    />
                    <label htmlFor="video-upload" className="cursor-pointer">
                      <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-gray-600">Click to upload video</p>
                      <p className="text-sm text-gray-400">MP4, MOV, AVI, WebM files supported</p>
                    </label>
                    {videoFile && (
                      <div className="mt-4 p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Video className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700">{videoFile.name}</span>
                          <span className="text-xs text-green-600">({(videoFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearVideo}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="video-url">Video URL</Label>
                    <Input
                      id="video-url"
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={videoUrl}
                      onChange={handleVideoUrlChange}
                    />
                    <p className="text-xs text-gray-500">
                      Supports YouTube, Vimeo, and other video platform links
                    </p>
                    {videoUrl && (
                      <div className="mt-2 p-2 bg-gray-50 rounded flex items-center justify-between">
                        <span className="text-sm text-gray-700 truncate">{videoUrl}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearVideo}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FileText className="mr-2 h-5 w-5" />
                  File Attachment (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  type="file"
                  onChange={handleAttachmentUpload}
                  className="hidden"
                  id="attachment-upload"
                />
                <label htmlFor="attachment-upload" className="block">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="mb-2 pointer-events-none"
                      asChild
                    >
                      <span className="flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload File
                      </span>
                    </Button>
                    <p className="text-sm text-gray-500 mt-2">or click anywhere to browse</p>
                    <p className="text-xs text-gray-400 mt-1">PDFs, documents, worksheets</p>
                  </div>
                </label>
                {attachmentFile && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">{attachmentFile.name}</span>
                      <span className="text-xs text-green-600">({(attachmentFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeAttachment}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Volume2 className="mr-2 h-5 w-5" />
                  Audio Content (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={audioUploadType === 'file' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAudioUploadType('file')}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload File
                  </Button>
                  <Button
                    type="button"
                    variant={audioUploadType === 'url' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAudioUploadType('url')}
                    className="flex items-center gap-2"
                  >
                    <Link className="h-4 w-4" />
                    Add URL
                  </Button>
                </div>

                {audioUploadType === 'file' ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={handleAudioUpload}
                      className="hidden"
                      id="audio-upload"
                    />
                    <label htmlFor="audio-upload" className="cursor-pointer">
                      <Mic className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-gray-600">Click to upload audio</p>
                      <p className="text-sm text-gray-400">MP3, WAV, OGG files supported</p>
                    </label>
                    {audioFile && (
                      <div className="mt-4 space-y-2">
                        <div className="p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Volume2 className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-700">{audioFile.name}</span>
                            <span className="text-xs text-green-600">({(audioFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={toggleAudioPreview}
                            >
                              {isAudioPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={clearAudio}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <audio
                          ref={(ref) => setAudioPreviewRef(ref)}
                          onEnded={() => setIsAudioPlaying(false)}
                          className="hidden"
                        >
                          <source src={URL.createObjectURL(audioFile)} type={audioFile.type} />
                        </audio>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="audio-url">Audio URL</Label>
                    <Input
                      id="audio-url"
                      type="url"
                      placeholder="https://example.com/audio.mp3"
                      value={audioUrl}
                      onChange={handleAudioUrlChange}
                    />
                    <p className="text-xs text-gray-500">
                      Direct audio file links or audio platform URLs
                    </p>
                    {audioUrl && (
                      <div className="mt-2 p-2 bg-gray-50 rounded flex items-center justify-between">
                        <span className="text-sm text-gray-700 truncate">{audioUrl}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearAudio}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !tutorProfileExists}>
              {isSubmitting ? 'Creating...' : 'Create Lesson'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLessonModal;
